import React from 'react';
import styled from 'styled-components';
import Tooltip from '@material-ui/core/Tooltip';
import ToolTip from 'components/i-tooltip';
import uuid from 'uuid';
import {
  BottomBottom,
  BottomTop,
  FormulaIcon,
  FormulaTable,
  OptionTable,
  OptionValueTableHeader,
} from 'components/deal-results/product-items/_common';
import { calculateNetOptionValueFromProductSummary } from 'lib/helpers/evaluation-functions';
import { formatNumber } from 'lib/helpers/currency';
import labels from 'constants/labels';
import messages from 'constants/messages';
import { ISummaryProduct, ISummaryPrice } from 'store/deal-evaluation/types';
import { IProductInterface } from './product-item';

export const ProductItemItemizedContainerStyled = styled.div``;

export interface IProductItemItemizedContainerProps {
  pricePerUnitLabel: string;
  p: IProductInterface;
  contractSalesPriceOutput: string;
  contractNetbackPriceOutput: string;
  contractMarginPriceOutput: string;
  baseSalesPriceOutput: string;
  baseNetbackPriceOutput: string;
  baseMarginPriceOutput: string;
  mainBatnaSalesPriceOutput: string;
  mainBatnaNetbackPriceOutput: string;
  mainBatnaMarginPriceOutput: string;
  floorValueOutput: string;
  capValueOutput: string;
  pullValueOutput: string;
  pushValueOutput: string;
  earlyTermValueOutput: string;
  extensionValueOutput: string;
  costToServeOutput: string;
  creditValueOutput: string;
  productSummary: ISummaryProduct;
  loading: boolean;
}

export default ({
  p,
  pricePerUnitLabel,
  contractSalesPriceOutput,
  contractNetbackPriceOutput,
  contractMarginPriceOutput,
  baseSalesPriceOutput,
  baseNetbackPriceOutput,
  baseMarginPriceOutput,
  mainBatnaSalesPriceOutput,
  mainBatnaNetbackPriceOutput,
  mainBatnaMarginPriceOutput,
  floorValueOutput,
  capValueOutput,
  pullValueOutput,
  pushValueOutput,
  earlyTermValueOutput,
  extensionValueOutput,
  costToServeOutput,
  creditValueOutput,
  productSummary,
  loading,
}: IProductItemItemizedContainerProps) => {
  const indexCount = 2;
  return (
    <ProductItemItemizedContainerStyled>
      <BottomTop>
        <FormulaTable>
          <thead>
            <tr>
              <th aria-label="empty-header-cell" />
              <th>FORMULA DISPLAY LABEL</th>
              <th>REVENUE ({pricePerUnitLabel})</th>
              <th>NETBACK ({pricePerUnitLabel})</th>
              <th>MARGIN ({pricePerUnitLabel})</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>CONTRACT</td>
              <td>
                <Tooltip title={p.basePrice.formula}>
                  <span>
                    <FormulaIcon>Σ</FormulaIcon>
                    <span>{p.basePrice.description}</span>
                  </span>
                </Tooltip>
              </td>
              <td>{contractSalesPriceOutput}</td>
              <td>{contractNetbackPriceOutput}</td>
              <td>{contractMarginPriceOutput}</td>
            </tr>
            <tr>
              <td>CONTRACT W/O OPTIONS</td>
              <td>
                <Tooltip title={p.basePrice.formula}>
                  <span>
                    <FormulaIcon>Σ</FormulaIcon>
                    <span>{p.basePrice.description}</span>
                  </span>
                </Tooltip>
              </td>
              <td>{baseSalesPriceOutput}</td>
              <td>{baseNetbackPriceOutput}</td>
              <td>{baseMarginPriceOutput}</td>
            </tr>
            <tr>
              <td>BATNA 1</td>
              <td>
                <Tooltip title={p.mainBatna.formula}>
                  <span>
                    <FormulaIcon>Σ</FormulaIcon>
                    <span>{p.mainBatna.description}</span>
                  </span>
                </Tooltip>
              </td>
              <td>{mainBatnaSalesPriceOutput}</td>
              <td>{mainBatnaNetbackPriceOutput}</td>
              <td>{mainBatnaMarginPriceOutput}</td>
            </tr>
            {p.otherBatnas.map((otherBatna: ISummaryPrice, index: number) => {
              if (!otherBatna?.sales || !otherBatna.netback) {
                return null;
              }
              const otherBatnaSalesPrice = otherBatna.sales.total.ave;
              const otherBatnaNetbackPrice = otherBatna.netback.total.ave;
              const otherBatnaMarginPrice = otherBatna.margin ? otherBatna.margin.total.ave : null;

              let otherBatnaSalesPriceOutput = messages.info.notApplicable;
              if (otherBatnaSalesPrice !== null) {
                otherBatnaSalesPriceOutput = loading
                  ? labels.general.ellipsis
                  : formatNumber(otherBatnaSalesPrice, 0, true);
              }
              let otherBatnaNetbackPriceOutput = messages.info.notApplicable;
              if (otherBatnaNetbackPrice !== null) {
                otherBatnaNetbackPriceOutput = loading
                  ? labels.general.ellipsis
                  : formatNumber(otherBatnaNetbackPrice, 0, true);
              }
              let otherBatnaMarginPriceOutput = messages.info.notApplicable;
              if (otherBatnaMarginPrice !== null) {
                otherBatnaMarginPriceOutput = loading
                  ? labels.general.ellipsis
                  : formatNumber(otherBatnaMarginPrice, 0, true);
              }

              return (
                <tr key={`other-batna-item-${uuid()}`}>
                  <td>BATNA {index + indexCount}</td>
                  <td>
                    <Tooltip title={otherBatna.formula}>
                      <span>
                        <FormulaIcon>Σ</FormulaIcon>
                        <span>{otherBatna.description}</span>
                      </span>
                    </Tooltip>
                  </td>
                  <td>{otherBatnaSalesPriceOutput}</td>
                  <td>{otherBatnaNetbackPriceOutput}</td>
                  <td>{otherBatnaMarginPriceOutput}</td>
                </tr>
              );
            })}
          </tbody>
        </FormulaTable>
      </BottomTop>
      <BottomBottom>
        <OptionTable>
          <thead>
            <tr>
              <OptionValueTableHeader>
                OPTION VALUE
                <ToolTip
                  top="2"
                  placement="top"
                  title="Unit option values are expressed relative to the base volume. Base volume excludes early termination volume."
                />
              </OptionValueTableHeader>
              <th>FLOOR</th>
              <th>CAP</th>
              <th>PULL</th>
              <th>PUSH</th>
              <th>EARLY TERMINATION</th>
              <th>EXTENSION</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{formatNumber(calculateNetOptionValueFromProductSummary(productSummary), indexCount, true)}</td>
              <td>{floorValueOutput}</td>
              <td>{capValueOutput}</td>
              <td>{pullValueOutput}</td>
              <td>{pushValueOutput}</td>
              <td>{earlyTermValueOutput}</td>
              <td>{extensionValueOutput}</td>
            </tr>
          </tbody>
        </OptionTable>
        <OptionTable>
          <thead>
            <tr>
              <th>COSTS</th>
              <th>CREDIT</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>{costToServeOutput}</td>
              <td>{creditValueOutput}</td>
            </tr>
          </tbody>
        </OptionTable>
      </BottomBottom>
    </ProductItemItemizedContainerStyled>
  );
};
